import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import {
  newChangeOrigin,
  runUndoable,
  SnapshotCache,
  type DocWriter,
  type Snapshot,
  type Undoable,
} from "@budgeter/shared";

export type ConnectionState = "offline" | "connecting" | "online";

export interface SyncStatus {
  /** IndexedDB copy has loaded (the app can render). */
  loaded: boolean;
  connection: ConnectionState;
  /** Local changes the connection is waiting for the server to confirm (this session only). */
  unsynced: number;
  /**
   * There may be local changes the server hasn't confirmed, including ones from an earlier
   * session (offline edits, then a reload). Use this for the badge and the logout warning.
   */
  hasUnsynced: boolean;
  /**
   * Changes from an earlier session are still waiting, so `unsynced` (this session's count)
   * undercounts: show no number.
   */
  unsyncedFromEarlier: boolean;
  /** The server refused the session; data is kept until the user logs in again. */
  authFailed: boolean;
  /** At least one full sync with the server has completed on this device. */
  everSynced: boolean;
}

export interface Actor {
  userId: string;
  deviceId: string;
}

const everSyncedKey = "budgeter.everSynced";
const confirmedKeyPrefix = "budgeter.confirmedState.";
export const localDbName = "budgeter-budget";

/**
 * The budget document on this device: IndexedDB first (instant, offline), then Hocuspocus
 * in the background (SPEC §9.2, §11.4). React reads it through `snapshot` + `subscribe`.
 */
export class BudgetStore {
  readonly doc = new Y.Doc();
  private readonly cache: SnapshotCache;
  private readonly persistence: IndexeddbPersistence | null;
  private provider: HocuspocusProvider | null = null;
  private socket: HocuspocusProviderWebsocket | null = null;
  private statusValue: SyncStatus;
  private readonly statusListeners = new Set<() => void>();
  private connectPending = false;
  private destroyed = false;
  /** The provider backs off up to 30s; reconnect as soon as the browser says it's online. */
  private readonly onOnline = () => {
    void this.socket?.connect();
  };

  constructor(
    private readonly actor: Actor,
    options: { persist?: boolean } = {},
  ) {
    this.cache = new SnapshotCache(this.doc, { deferValidation: options.persist !== false });
    this.statusValue = {
      loaded: options.persist === false,
      connection: "offline",
      unsynced: 0,
      hasUnsynced: false,
      unsyncedFromEarlier: false,
      authFailed: false,
      everSynced: safeGet(everSyncedKey) === "1",
    };
    this.persistence =
      options.persist === false ? null : new IndexeddbPersistence(localDbName, this.doc);
    this.persistence?.whenSynced.then(() => {
      const earlier = this.pendingLocally();
      this.setStatus({ loaded: true, unsyncedFromEarlier: earlier, hasUnsynced: earlier });
    });
    if (this.persistence) {
      this.doc.on("update", this.onDocUpdate);
    }
  }

  private get confirmedKey(): string {
    return confirmedKeyPrefix + this.actor.userId;
  }

  private readonly onDocUpdate = (_update: Uint8Array, origin: unknown): void => {
    const p = this.provider;
    if (p && origin === p && p.isSynced && p.unsyncedChanges === 0) {
      this.confirm();
      return;
    }
    this.setStatus({ hasUnsynced: this.statusValue.unsynced > 0 || this.pendingLocally() });
  };

  /**
   * Whether this device's document has updates beyond the state the server last confirmed.
   * Comparing state vectors is enough because every command also inserts (its `_chg` stamp and
   * changeset entry), so no local change is a pure delete.
   */
  private pendingLocally(): boolean {
    const current = Y.decodeStateVector(Y.encodeStateVector(this.doc));
    const raw = safeGet(this.confirmedKey);
    if (raw === null) {
      return current.size > 0;
    }
    let confirmed: Map<number, number>;
    try {
      confirmed = Y.decodeStateVector(base64ToBytes(raw));
    } catch {
      return current.size > 0;
    }
    for (const [client, clock] of current) {
      if (clock > (confirmed.get(client) ?? 0)) {
        return true;
      }
    }
    return false;
  }

  /** The server has everything this document holds: remember that across reloads. */
  private confirm(): void {
    if (this.persistence) {
      safeSet(this.confirmedKey, bytesToBase64(Y.encodeStateVector(this.doc)));
    }
    this.setStatus({ hasUnsynced: false, unsyncedFromEarlier: false });
  }

  private onUnsyncedChanges(count: number): void {
    this.setStatus({ unsynced: count });
    if (count === 0) {
      this.confirm();
    } else {
      this.setStatus({ hasUnsynced: true });
    }
  }

  get snapshot(): Snapshot {
    return this.cache.snapshot;
  }

  subscribe = (listener: () => void): (() => void) => this.cache.subscribe(listener);

  get status(): SyncStatus {
    return this.statusValue;
  }

  subscribeStatus = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  /** Runs a shared command as this user and device. Returns the result and an undo. */
  run<R>(description: string, fn: (w: DocWriter) => R): Undoable<R> {
    return runUndoable(
      this.doc,
      newChangeOrigin(description, this.actor.userId, this.actor.deviceId),
      fn,
    );
  }

  undo<R>(change: Undoable<R>, description: string): boolean {
    return change.undo(
      newChangeOrigin(`Undo: ${description}`, this.actor.userId, this.actor.deviceId),
    );
  }

  /** Starts background sync. Call after the first render so nothing waits on the network. */
  connect(url = defaultSyncUrl(this.actor.deviceId)): void {
    if (this.provider || this.connectPending) {
      return;
    }
    // The provider must attach after IndexedDB has loaded: otherwise it counts the local load
    // as an unsynced change that, offline, is never acknowledged.
    this.connectPending = true;
    const loaded = this.persistence ? this.persistence.whenSynced : Promise.resolve();
    void loaded.then(() => {
      this.connectPending = false;
      if (!this.destroyed) {
        this.attachProvider(url);
      }
    });
  }

  private attachProvider(url: string): void {
    this.socket = new HocuspocusProviderWebsocket({
      url,
      onStatus: ({ status }) => {
        this.setStatus({
          connection:
            status === "connected" ? "online" : status === "connecting" ? "connecting" : "offline",
        });
      },
    });
    this.provider = new HocuspocusProvider({
      websocketProvider: this.socket,
      name: "budget",
      document: this.doc,
      token: "session",
      onAuthenticationFailed: () => this.setStatus({ authFailed: true }),
      onAuthenticated: () => this.setStatus({ authFailed: false }),
      onSynced: () => {
        safeSet(everSyncedKey, "1");
        this.setStatus({ everSynced: true });
      },
      onUnsyncedChanges: ({ number }) => this.onUnsyncedChanges(number),
    });
    this.provider.attach();
    window.addEventListener("online", this.onOnline);
  }

  /** Reconnects after logging in again; local changes are kept and pushed. */
  reconnect(): void {
    this.disconnect();
    this.setStatus({ authFailed: false });
    this.connect();
  }

  disconnect(): void {
    window.removeEventListener("online", this.onOnline);
    this.provider?.destroy();
    this.socket?.destroy();
    this.provider = null;
    this.socket = null;
    this.setStatus({ connection: "offline" });
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    this.disconnect();
    this.cache.destroy();
    this.doc.off("update", this.onDocUpdate);
    await this.persistence?.destroy();
    this.doc.destroy();
  }

  /** Deletes every local copy (log out, SPEC §11.4). */
  async wipe(): Promise<void> {
    this.destroyed = true;
    this.disconnect();
    this.cache.destroy();
    if (this.persistence) {
      await this.persistence.clearData();
    }
    this.doc.destroy();
    safeRemove(everSyncedKey);
    safeRemove(this.confirmedKey);
  }

  private setStatus(change: Partial<SyncStatus>): void {
    const next = { ...this.statusValue, ...change };
    if (
      Object.keys(change).every(
        (k) => next[k as keyof SyncStatus] === this.statusValue[k as keyof SyncStatus],
      )
    ) {
      return;
    }
    this.statusValue = next;
    for (const l of this.statusListeners) {
      l();
    }
  }
}

function defaultSyncUrl(device: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/api/sync?device=${encodeURIComponent(device)}`;
}

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToBytes(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); the flag only affects a hint.
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // As above.
  }
}
